import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ;

// Helper function to get auth token and headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('token');
  if (!token) {
    console.error('❌ No authentication token found');
    throw new Error('Please log in to access notes');
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Helper function to handle auth errors
const handleAuthError = (error) => {
  if (error.response && error.response.status === 401) {
    console.error('❌ Authentication failed - redirecting to login');
    sessionStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }
  throw error;
};

export const notesService = {
  // Get notes for a specific document
  getNotes: async (documentTitle) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/pdfs/${documentTitle}/notes`, {
        headers: getAuthHeaders()
      });
      console.log('📝 GET Notes Response:', response.data);
      return response.data;
    } catch (error) {
      // If 404, return empty notes (document doesn't exist yet for this user)
      if (error.response && error.response.status === 404) {
        console.log('📝 No existing notes found for this user, starting fresh');
        return {
          content: '',
          updatedAt: null,
          documentTitle
        };
      }
      handleAuthError(error);
    }
  },

  // Save notes for a specific document
  saveNotes: async (documentTitle, notesContent) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/pdfs/${documentTitle}/notes`, 
        {
          content: notesContent,
          updatedAt: new Date().toISOString()
        },
        {
          headers: getAuthHeaders()
        }
      );
      console.log('💾 Save Notes Response:', response.data);
      return response.data;
    } catch (error) {
      handleAuthError(error);
    }
  },

  // Generate PDF from notes and trigger download
  downloadNotes: async (documentTitle, notesContent) => {
    try {
      console.log('📥 Downloading notes as PDF...', { 
        documentTitle, 
        contentLength: notesContent.length,
        userId: 'authenticated' // User is authenticated via token
      });
      
      const response = await axios.post(
        `${API_BASE_URL}/pdfs/${documentTitle}/notes/download`,
        { content: notesContent },
        { 
          headers: getAuthHeaders(),
          responseType: 'blob',
          timeout: 30000 // 30 second timeout for PDF generation
        }
      );
      
      console.log('✅ PDF generated successfully, creating download...');
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename: documentTitle_notes_currentDate.pdf
      const currentDate = new Date().toISOString().split('T')[0];
      const filename = `${documentTitle}_notes_${currentDate}.pdf`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Notes downloaded successfully:', filename);
      return true;
    } catch (error) {
      console.error('❌ Error downloading notes:', error);
      
      // Handle authentication errors
      if (error.response && error.response.status === 401) {
        sessionStorage.removeItem('token');
        window.location.href = '/login';
        throw new Error('Session expired. Please log in again.');
      }
      
      // Provide more specific error messages
      if (error.code === 'ECONNABORTED') {
        throw new Error('Download timeout - PDF generation took too long');
      } else if (error.response && error.response.status === 400) {
        throw new Error('No notes content to download');
      } else if (error.response && error.response.status === 404) {
        throw new Error('Document not found for your account');
      } else if (error.response && error.response.status === 500) {
        throw new Error('Server error during PDF generation');
      } else {
        throw new Error('Failed to download notes PDF');
      }
    }
  },

  // Delete notes for a specific document
  deleteNotes: async (documentTitle) => {
    try {
      const response = await axios.delete(`${API_BASE_URL}/pdfs/${documentTitle}/notes`, {
        headers: getAuthHeaders()
      });
      console.log('🗑️ Delete Notes Response:', response.data);
      return response.data;
    } catch (error) {
      handleAuthError(error);
    }
  }
};